import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import {
  getDefaultTab,
  getTabLabel,
  getTabsForContext,
  normalizeTabKey,
  type TabKeyFor,
} from './settingsTabs';
import { type SettingsContext } from './config/settingsContext';
import { useSettingsUnsavedChanges } from './SettingsUnsavedChangesContext';

const TAB_QUERY_KEY = 'tab';

type Props<C extends SettingsContext> = {
  context: C;
  active?: TabKeyFor<C>;
  onChange?: (tab: TabKeyFor<C>) => void;
  hiddenTabs?: readonly TabKeyFor<C>[];
};

export function SettingsTabBar<C extends SettingsContext>({
  context,
  active,
  onChange,
  hiddenTabs,
}: Props<C>): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get(TAB_QUERY_KEY);
  const resolvedTab = normalizeTabKey(context, rawTab);

  const tabs = getTabsForContext(context);
  const visibleTabs: readonly TabKeyFor<C>[] = hiddenTabs?.length
    ? tabs.filter(tab => !hiddenTabs.includes(tab))
    : tabs;
  const isResolvedHidden = hiddenTabs?.includes(resolvedTab) ?? false;

  // Redirect when ?tab is invalid or hidden. Loop-break: rawTab === targetTab.
  // Check hidden first so an invalid ?tab that normalizes to a hidden default
  // lands on a visible tab in one redirect, not two.
  const targetTab: TabKeyFor<C> = isResolvedHidden
    ? (visibleTabs[0] ?? getDefaultTab(context))
    : resolvedTab;

  const activeTab: TabKeyFor<C> = active ?? targetTab;

  const { confirmNavigation } = useSettingsUnsavedChanges();

  const tabRefs = useRef<
    Partial<Record<TabKeyFor<C>, HTMLButtonElement | null>>
  >({});

  useEffect(() => {
    if (rawTab === targetTab) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set(TAB_QUERY_KEY, targetTab);
    setSearchParams(next, { replace: true });
  }, [rawTab, targetTab, searchParams, setSearchParams]);

  const handleSelect = (tab: TabKeyFor<C>): void => {
    if (tab === activeTab) {
      return;
    }

    // Route through the unsaved-changes guard: if the active panel has unsaved
    // changes, this defers the switch behind a "Discard unsaved changes?"
    // confirmation (spec KIR-3253 #14). No-op passthrough when nothing is dirty.
    confirmNavigation(() => {
      trackEvent('settings_tab_opened', { tab, context });

      if (onChange) {
        onChange(tab);
      }

      // Always update the URL on user click; do NOT use replace so back/forward navigates.
      const next = new URLSearchParams(searchParams);
      next.set(TAB_QUERY_KEY, tab);
      setSearchParams(next);
    });
  };

  // WAI-ARIA tabs, automatic activation: arrow keys move focus+selection; wrap at ends.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (visibleTabs.length === 0) {
      return;
    }

    // Skip modifier combos so we don't hijack Cmd+ArrowRight (browser back),
    // Shift+arrow (text selection), etc.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    const currentIdx = visibleTabs.indexOf(activeTab);
    let nextIdx: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % visibleTabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIdx =
        currentIdx < 0
          ? 0
          : (currentIdx - 1 + visibleTabs.length) % visibleTabs.length;
    } else if (event.key === 'Home') {
      nextIdx = 0;
    } else if (event.key === 'End') {
      nextIdx = visibleTabs.length - 1;
    }

    if (nextIdx === null) {
      return;
    }

    event.preventDefault();
    const nextTab = visibleTabs[nextIdx];
    handleSelect(nextTab);
    tabRefs.current[nextTab]?.focus();
  };

  return (
    <nav
      role="tablist"
      aria-label="Settings sub-tabs"
      data-settings-tabbar
      aria-orientation="horizontal"
      className="sticky top-16 z-20 flex w-full overflow-x-auto overflow-y-hidden border-b border-border bg-background"
    >
      {visibleTabs.map(tab => {
        const isActive = tab === activeTab;
        return (
          <button
            key={tab}
            ref={el => {
              tabRefs.current[tab] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`settings-panel-${tab}`}
            id={`settings-tab-${tab}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => handleSelect(tab)}
            onKeyDown={handleKeyDown}
            className={cn(
              'px-5 py-3 text-[13px] border-b-2 transition-colors whitespace-nowrap',
              isActive
                ? 'text-accent-foreground border-accent-foreground font-semibold'
                : 'text-muted-foreground border-transparent font-normal hover:text-foreground'
            )}
          >
            {getTabLabel(tab)}
          </button>
        );
      })}
    </nav>
  );
}
